import { useMemo } from 'react';

import { resolveFlagsAt } from '@core/settings/flag-layers';
import { useUserScopeChain } from '@core/settings/useSettings';
import { useFeatureFlagStore } from '@core/state/feature-flags.store';
import { useSettingsStore } from '@core/state/settings.store';
import { useUserStore } from '@core/state/user.store';

import type { Capability } from './capabilities';
import {
  can,
  canAny,
  hasFeature,
  resolveEntitlements,
  visibleFeatures,
  type Entitlements
} from './entitlements';
import type { FeatureDefinition, FeatureId } from './features';
import type { FlagId, ResolvedFlags } from './flags';

/**
 * The hooks screens use. Nothing in a component should reach past these to a
 * role name - that is the whole point of the capability list.
 *
 * `useEntitlements` memoises on the user object rather than on the roles
 * array, because the store replaces the whole `UserData` on every write and a
 * new array identity each render would rebuild both sets for nothing. The
 * flags are memoised on their inputs for the same reason.
 */

/**
 * The flags in force for the signed-in user: the shipped baseline, overridden
 * by the saved settings at Global, their region and their country.
 */
export function useResolvedFlags(): ResolvedFlags {
  const environment = useFeatureFlagStore((state) => state.environment);
  const deployment = useFeatureFlagStore((state) => state.deployment);
  const deploymentScopes = useFeatureFlagStore((state) => state.deploymentScopes);
  const document = useSettingsStore((state) => state.document);
  const chain = useUserScopeChain();

  return useMemo(
    () => resolveFlagsAt({ environment, deployment, deploymentScopes }, document, chain),
    [environment, deployment, deploymentScopes, document, chain]
  );
}

export function useEntitlements(): Entitlements {
  const userData = useUserStore((state) => state.userData);
  const flags = useResolvedFlags().enabled;

  return useMemo(() => resolveEntitlements(userData, flags), [userData, flags]);
}

/**
 * `const showAssistant = useFlag('assistant')`.
 *
 * Only for a switch with no capability behind it. Where a flag gates a
 * capability, ask for the capability with `useCan` - it already accounts for
 * the flag, and also for who the user is.
 */
export function useFlag(flag: FlagId): boolean {
  return useResolvedFlags().enabled.has(flag);
}

/** `const canExport = useCan('assignments.export')`. */
export function useCan(capability: Capability | null | undefined): boolean {
  const entitlements = useEntitlements();

  return can(entitlements, capability);
}

export function useCanAny(capabilities: readonly Capability[]): boolean {
  const entitlements = useEntitlements();

  return canAny(entitlements, capabilities);
}

export function useHasFeature(feature: FeatureId): boolean {
  const entitlements = useEntitlements();

  return hasFeature(entitlements, feature);
}

export function useVisibleFeatures(): FeatureDefinition[] {
  const entitlements = useEntitlements();

  return useMemo(() => visibleFeatures(entitlements), [entitlements]);
}

/**
 * A predicate to pass down to something that is not a component - the workflow
 * table, a column definition builder, a grid's cell renderer.
 */
export function useCanFn(): (capability: Capability) => boolean {
  const entitlements = useEntitlements();

  return useMemo(() => (capability: Capability) => can(entitlements, capability), [entitlements]);
}
